import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import type { ICategoriaProduto } from "interfaces/loja";
import { CATEGORIAS_ACCENT, actionIconSx } from "../categorias.theme";
import { highlightMatch } from "./categorias.helpers";

type Props = {
  categoria: ICategoriaProduto;
  level?: number;
  expanded: Set<number>;
  searchTerm: string;
  onToggle: (id: number) => void;
  onSelect: (cat: ICategoriaProduto) => void;
  selectedId?: number;
  onEdit: (cat: ICategoriaProduto) => void;
  onDelete: (cat: ICategoriaProduto) => void;
  onAddSub: (cat: ICategoriaProduto) => void;
};

export default function CategoryTreeItem({
  categoria,
  level = 0,
  expanded,
  searchTerm,
  onToggle,
  onSelect,
  selectedId,
  onEdit,
  onDelete,
  onAddSub,
}: Props) {
  const hasChildren = Boolean(categoria.subcategorias?.length);
  const isExpanded = expanded.has(categoria.id);
  const isSelected = selectedId === categoria.id;
  const subCount = categoria.subcategorias?.length ?? 0;
  const isMatch = highlightMatch(categoria.nome, searchTerm);

  return (
    <>
      <ListItem
        disablePadding
        sx={{
          pl: level * 1.5,
          mb: 0.25,
        }}
      >
        <ListItemButton
          onClick={() => {
            onSelect(categoria);
            if (hasChildren) onToggle(categoria.id);
          }}
          selected={isSelected}
          sx={{
            borderRadius: 1.5,
            py: 0.75,
            px: 1,
            borderLeft: isSelected ? `3px solid ${CATEGORIAS_ACCENT}` : "3px solid transparent",
            bgcolor: isSelected ? "rgba(249, 96, 48, 0.06)" : isMatch ? "rgba(249, 96, 48, 0.03)" : "transparent",
            "&:hover": {
              bgcolor: isSelected ? "rgba(249, 96, 48, 0.08)" : "rgba(249, 96, 48, 0.04)",
            },
            "&.Mui-selected": {
              bgcolor: "rgba(249, 96, 48, 0.06)",
            },
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggle(categoria.id);
              }}
              sx={{ mr: 0.5, p: 0.25 }}
            >
              {isExpanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              )}
            </IconButton>
          ) : (
            <Box width={28} mr={0.5} />
          )}

          <FolderOutlinedIcon
            sx={{
              fontSize: 18,
              mr: 1,
              color: isSelected ? CATEGORIAS_ACCENT : "text.secondary",
              flexShrink: 0,
            }}
          />

          <ListItemText
            primary={
              <MDBox display="flex" alignItems="center" gap={1} minWidth={0}>
                <MDTypography
                  variant="body2"
                  fontWeight={isSelected ? "bold" : "regular"}
                  noWrap
                  sx={{ color: isMatch ? CATEGORIAS_ACCENT : undefined }}
                >
                  {categoria.nome}
                </MDTypography>
                {subCount > 0 ? (
                  <Chip
                    size="small"
                    label={subCount}
                    sx={{
                      height: 20,
                      fontSize: "0.65rem",
                      bgcolor: "rgba(249, 96, 48, 0.12)",
                      color: CATEGORIAS_ACCENT,
                      fontWeight: 700,
                    }}
                  />
                ) : null}
              </MDBox>
            }
            secondary={
              categoria.descricao ? (
                <MDTypography variant="caption" color="text.secondary" noWrap display="block">
                  {categoria.descricao}
                </MDTypography>
              ) : null
            }
          />

          <MDBox display="flex" flexShrink={0} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Adicionar subcategoria">
              <IconButton size="small" onClick={() => onAddSub(categoria)} sx={actionIconSx}>
                <AddOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit(categoria)} sx={actionIconSx}>
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Excluir">
              <IconButton
                size="small"
                onClick={() => onDelete(categoria)}
                sx={{ color: "error.main" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </MDBox>
        </ListItemButton>
      </ListItem>

      {hasChildren ? (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {categoria.subcategorias!.map((sub) => (
              <CategoryTreeItem
                key={sub.id}
                categoria={sub}
                level={level + 1}
                expanded={expanded}
                searchTerm={searchTerm}
                onToggle={onToggle}
                onSelect={onSelect}
                selectedId={selectedId}
                onEdit={onEdit}
                onDelete={onDelete}
                onAddSub={onAddSub}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
    </>
  );
}
